import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useFirestoreConnect } from 'react-redux-firebase'

import { useAuth } from 'hooks/use-auth'

export enum orgFeatures {
    showPayments = 'showPayments',
    showUsage = 'showUsage',
    enableExtendedInsights = 'enableExtendedInsights',
    disableExternalSharing = 'disableExternalSharing',
    transcriptAnalyzerEnabled = 'transcriptAnalyzerEnabled',
    enableKafkaForAnalytics = 'enableKafkaForAnalytics',
    overrideAllowedLanguages = 'overrideAllowedLanguages',
    isAskGuiddeShown = 'isAskGuiddeShown',
    uploadSubtitles = 'uploadSubtitles',
    enableVoiceOver = 'enableVoiceOver',
    showApplicationsTab = 'showApplicationsTab',
    showQGRecordOnly = 'showQGRecordOnly',
    showQgSubtitles = 'showQgSubtitles',
    showDefaultCoverColors = 'showDefaultCoverColors',
    enableSketchedArrow = 'enableSketchedArrow',
    removeOverlayInDoc = 'removeOverlayInDoc',
    showCaptions = 'showCaptions',
    canvasV2 = 'canvasV2',
    enableComments = 'enableComments',
    allowMagicCapture = 'allowMagicCapture',
    showMagicCaptureLanguages = 'showMagicCaptureLanguages',
    hideWatchOnGuiddeInEmbed = 'hideWatchOnGuiddeInEmbed'
}
export enum globalFeatures {
    qgTransitions = 'qgTransitions',
    enableNgnixLinks = 'enableNgnixLinks',
    useTeams = 'useTeams'
}

type FeatureFlagsType<T extends string> = {
    [key in T]?: boolean
} & { isLoading: boolean }

// orgId can be any organizationId, not only your own.
// SuperAdmin has ability to see/change featureFlags of any organization
export const useOrgFeatureFlags = (orgId?: string) => {
    const auth = useAuth()

    const organizationId = orgId || auth.orgId
    useFirestoreConnect({
        collection: organizationId ? 'featureFlags' : 'empty',
        doc: organizationId ?? null
    })

    const selector = useSelector(state => state.firestore?.data?.featureFlags)

    const featureFlags = useMemo(() => selector?.[organizationId] || {}, [organizationId, selector])

    return useMemo(
        () => ({
            ...Object.fromEntries(
                Object.keys(orgFeatures).map(feature => [feature, featureFlags?.[feature]])
            ),
            isLoading: selector === undefined
        }),
        [featureFlags, selector]
    ) as FeatureFlagsType<orgFeatures>
}

export const useGlobalFeatureFlags = () => {
    // Since we are not logged in (login page) and we *MUST* use a global feature flag
    // we retrieve the values without auth, permissions were changed in firestore as well
    useFirestoreConnect({
        collection: 'featureFlags',
        doc: 'globalFeatureFlags'
    })

    const selector = useSelector(state => state.firestore?.data?.featureFlags)

    const featureFlags = useMemo(() => selector?.globalFeatureFlags, [selector])

    return useMemo(
        () => ({
            ...Object.fromEntries(
                Object.keys(globalFeatures).map(feature => [feature, featureFlags?.[feature]])
            ),
            isLoading: selector === undefined // it will be null if empty
        }),
        [featureFlags, selector]
    ) as FeatureFlagsType<globalFeatures>
}
